import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader, LayoutGrid } from 'lucide-react';
import { setAllowedComponents } from '../../../services/apiClient';
import { AVAILABLE_COMPONENTS } from '../../misc/ComponentLibrary/ComponentLibrary';
import { Button } from '../../../components/ui/button';

// Same "active" definition ComponentLibrary itself uses to decide what a user
// can actually see/add to a dashboard — Archive tiles are already unreachable
// from the tile picker, so they are not offered here either. Known, deliberately
// out-of-scope pre-existing bug: 'resource-calendar' is tagged category "New"
// (active) despite living in ComponentLibrary.jsx's Archive section, so it will
// appear here as a restrictable tile. Do not "fix" this by touching
// ComponentLibrary.jsx's category tagging as a side effect of this file.
const ACTIVE_TILES = AVAILABLE_COMPONENTS.filter((c) => c.category !== 'Archive');

// Category order mirrors ComponentLibrary's own grouping: first-seen order
// across ACTIVE_TILES, exactly like ComponentLibrary's `[...new Set(...)]`.
const CATEGORIES = [...new Set(ACTIVE_TILES.map((c) => c.category))];

/**
 * Tile & Tab Access tab of the tenant admin console — lets an admin restrict a
 * customer to a subset of dashboard tiles by writing customers.allowed_component_ids
 * (Task 6's setAllowedComponents). Mirrors FileAccessEditor's 'all' / 'restricted'
 * mode-radio pattern (frontend/src/features/file-manager/OCELFileManager/FileAccessEditor.jsx),
 * applied to tile ids instead of customer ids.
 *
 * allowed_component_ids semantics (matches backend/src/tenant_admin/schemas.py):
 *   - null            -> unrestricted, customer can use every active tile ('all')
 *   - [] (empty array) -> restricted to NO tiles (a valid, distinct 'restricted' state)
 *   - [ids]           -> restricted to exactly those tiles
 */
const TileAccessTab = ({ customerId, customer, onSaved }) => {
  const { t } = useTranslation('admin');

  const [mode, setMode] = useState(() =>
    customer?.allowed_component_ids == null ? 'all' : 'restricted',
  );
  const [selected, setSelected] = useState(() => new Set(customer?.allowed_component_ids ?? []));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const toggle = (tileId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tileId)) {
        next.delete(tileId);
      } else {
        next.add(tileId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setErr(null);
    try {
      const payload = mode === 'all' ? null : Array.from(selected);
      await setAllowedComponents(customerId, payload);
      onSaved?.(payload);
    } catch (e) {
      setErr(t('errors.saveTileAccess', { detail: e.message }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex-1">
      <h2 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
        <LayoutGrid className="w-4 h-4 text-primary-600" /> {t('tileAccess.title')}
      </h2>
      {err && <div className="mb-3 text-sm text-error-600">{err}</div>}

      <div className="space-y-2 text-sm text-gray-700 mb-4 border border-gray-200 rounded-lg p-3 bg-white">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="tile-access-mode"
            checked={mode === 'all'}
            onChange={() => setMode('all')}
          />
          {t('tileAccess.allTiles')}
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="tile-access-mode"
            checked={mode === 'restricted'}
            onChange={() => setMode('restricted')}
          />
          {t('tileAccess.restricted')}
        </label>
      </div>

      <div className="space-y-3 mb-4">
        {CATEGORIES.map((category) => (
          <div key={category} className="border border-gray-200 rounded-lg p-3 bg-white">
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-1.5">{category}</h3>
            <div className="space-y-1">
              {ACTIVE_TILES.filter((tile) => tile.category === category).map((tile) => (
                <label
                  key={tile.id}
                  className={`flex items-center gap-2 ${
                    mode === 'all' ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer text-gray-700'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(tile.id)}
                    disabled={mode === 'all'}
                    onChange={() => toggle(tile.id)}
                  />
                  <span>{tile.name}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Button onClick={handleSave} disabled={saving} size="sm">
        {saving && <Loader className="w-3.5 h-3.5 animate-spin mr-1.5" />}
        {t('tileAccess.save')}
      </Button>
    </div>
  );
};

export default TileAccessTab;
